import { useState } from 'react';
import { CollapsibleCard, type ToolRendererProps } from '../ToolCard';

interface ListItem {
  id?: number | string;
  title?: string;
  name?: string;
  status?: string;
  [k: string]: unknown;
}

function pickTitle(item: ListItem): string {
  return (
    (item.title as string | undefined) ||
    (item.name as string | undefined) ||
    (item.id != null ? `#${item.id}` : 'item')
  );
}

function pickStatus(item: ListItem): string | null {
  const s = item.status;
  return typeof s === 'string' ? s : null;
}

function statusColor(status: string): string {
  switch (status) {
    case 'completed':
    case 'active':
      return 'bg-moss/20 text-moss';
    case 'in_progress':
      return 'bg-gold/20 text-gold';
    case 'blocked':
    case 'critical':
    case 'cancelled':
      return 'bg-red-100 text-red-700';
    default:
      return 'bg-fog text-bark';
  }
}

export function RowanListCard({ toolName, result }: ToolRendererProps) {
  const [expanded, setExpanded] = useState(false);

  // Normalize response — Rowan endpoints return either array directly or
  // {tasks|data|items|...}. Fall back to [] if we can't find a list.
  let items: ListItem[] = [];
  if (Array.isArray(result)) {
    items = result as ListItem[];
  } else if (result && typeof result === 'object') {
    const obj = result as Record<string, unknown>;
    for (const k of ['tasks', 'data', 'items', 'projects', 'goals', 'kpis', 'memories', 'decisions', 'lessons', 'observations']) {
      if (Array.isArray(obj[k])) {
        items = obj[k] as ListItem[];
        break;
      }
    }
  }

  const visible = expanded ? items : items.slice(0, 5);
  const hiddenCount = items.length - visible.length;

  return (
    <CollapsibleCard toolName={toolName} defaultOpen>
      <div className="text-[11px] text-bark">
        {items.length} {items.length === 1 ? 'result' : 'results'}
      </div>
      <ul className="mt-1 space-y-1">
        {visible.map((item, i) => {
          const status = pickStatus(item);
          return (
            <li
              key={(item.id as string | number | undefined) ?? i}
              className="flex items-start gap-2 rounded px-1 py-1"
            >
              <span className="mt-0.5 h-1.5 w-1.5 shrink-0 rounded-full bg-gold" />
              <span className="flex-1 text-xs text-espresso">{pickTitle(item)}</span>
              {status && (
                <span className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium ${statusColor(status)}`}>
                  {status}
                </span>
              )}
            </li>
          );
        })}
      </ul>
      {hiddenCount > 0 && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="mt-1 text-[11px] text-gold hover:underline"
        >
          Show {hiddenCount} more
        </button>
      )}
    </CollapsibleCard>
  );
}
